import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Sliders } from "lucide-react";
import { useSEO } from "../lib/useSEO";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const categorySlug = params.get("category");
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const seoTitle = q
    ? `Search "${q}" | Joy Pearl`
    : activeCategory
    ? `${activeCategory.name} | Joy Pearl`
    : "Shop All Products | Joy Pearl";
  useSEO({
    title: seoTitle,
    description: q
      ? `Search results for "${q}" on Joy Pearl. Hand-curated artificial jewellery and kids' gifts. COD across India.`
      : "Shop the full Joy Pearl collection — artificial jewellery, earrings, necklaces, bangles, soft toys and kids' gifts. Cash on Delivery available across India.",
  });

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (activeCategory) qp.set("category", activeCategory.id);
    if (q) qp.set("q", q);
    if (sort) qp.set("sort", sort);
    api.get(`/products?${qp.toString()}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [activeCategory, q, sort]);

  const setSort = (v) => { params.set("sort", v); setParams(params); };
  const setCategory = (slug) => {
    if (slug) params.set("category", slug); else params.delete("category");
    setParams(params);
    setFilterOpen(false);
  };

  return (
    <div className="jp-container py-8 md:py-12" data-testid="products-page">
      <nav className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-4">
        <Link to="/" className="hover:text-brand-primary">Home</Link> / Shop {activeCategory && <> / <span className="text-brand-ink">{activeCategory.name}</span></>}
      </nav>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-5xl text-brand-ink">
            {q ? `Search: "${q}"` : activeCategory?.name || "All Products"}
          </h1>
          <p className="text-sm text-brand-muted mt-2">{products.length} item{products.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setFilterOpen((v) => !v)} className="md:hidden jp-btn-outline px-4 py-2 text-xs" data-testid="filter-toggle">
            <Sliders size={14} className="mr-2"/> Filters
          </button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="jp-input border border-brand-line px-3 py-2 text-sm" data-testid="sort-select">
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        <aside className={`${filterOpen ? "block" : "hidden"} md:block`}>
          <h3 className="font-serif text-lg mb-4">Categories</h3>
          <ul className="space-y-2 text-sm">
            <li>
              <button onClick={() => setCategory("")} data-testid="filter-all"
                className={`hover:text-brand-primary ${!activeCategory ? "text-brand-primary font-medium" : "text-brand-ink"}`}>All Products</button>
            </li>
            {categories.map((c) => (
              <li key={c.id}>
                <button onClick={() => setCategory(c.slug)} data-testid={`filter-cat-${c.slug}`}
                  className={`hover:text-brand-primary ${activeCategory?.id === c.id ? "text-brand-primary font-medium" : "text-brand-ink"}`}>{c.name}</button>
              </li>
            ))}
          </ul>
        </aside>

        <section>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-white border border-brand-line animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-products">
              <p className="font-serif text-2xl text-brand-ink">Nothing here yet.</p>
              <p className="text-sm text-brand-muted mt-2">Try a different search or category.</p>
              <Link to="/products" className="jp-btn-primary mt-6 inline-flex">Browse All</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="products-grid">
              {products.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
